import { InlineFormattedNumber, Text, Tooltip } from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import type { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';
import type { FundingRateRow } from './FundingRateBlotter.types';
import { FundingRateCellTooltip } from './FundingRateCellTooltip';
import { fundingRateHeatColor } from './fundingRateHeatColor';
import { isFlatRate, rateSignVariant } from './fundingRateSign';
import { FUNDING_RATE_DISPLAY_INCREMENT, OPEN_INTEREST_NUMBER_PROPS } from './tokens';

/** Shown when a cell has no value, so "no data" reads as intentional rather than a blank glitch. */
const NO_VALUE_PLACEHOLDER = '—';

/** Fills the List cell so the whole cell is hoverable, not just the (right-aligned, content-sized) number. */
const LIST_TOOLTIP_TARGET_STYLE: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
};

/** Fills the Matrix cell so the whole heat chip area is hoverable. */
const MATRIX_TOOLTIP_TARGET_STYLE: CSSProperties = { width: '100%', height: '100%', display: 'flex' };

interface FundingRateHoverTooltipProps {
  symbol: string;
  exchangeDisplayName: string | undefined;
  fundingRatePct: number;
  openInterestUsd: number | null;
  /** Current toolbar update-mode, resolved to its display label (e.g. "1H"/"1D"). */
  periodLabel: string;
  /** Card padding, resolved once from the theme by the columns hook rather than per cell. */
  tooltipPadding: number;
  targetStyle: CSSProperties;
  children: ReactNode;
}

/** Shared hover-card wrapper for the List "Current Rate" cell and a Matrix heat chip. */
function FundingRateHoverTooltip({
  symbol,
  exchangeDisplayName,
  fundingRatePct,
  openInterestUsd,
  periodLabel,
  tooltipPadding,
  targetStyle,
  children,
}: FundingRateHoverTooltipProps) {
  return (
    <Tooltip
      tooltip={
        <FundingRateCellTooltip
          symbol={symbol}
          exchangeDisplayName={exchangeDisplayName}
          fundingRatePct={fundingRatePct}
          openInterestUsd={openInterestUsd}
          periodLabel={periodLabel}
        />
      }
      tooltipTestID="funding-rate-cell-tooltip"
      targetStyle={targetStyle}
      contentStyle={{ padding: tooltipPadding }}
    >
      {children}
    </Tooltip>
  );
}

interface FundingRateCellProps extends ICellRendererParams<FundingRateRow, number | null> {
  /** Current toolbar update-mode, resolved to its display label (e.g. "1H"/"1D") — shown in the hover card. */
  periodLabel: string;
  /** Card padding, resolved once from the theme by the columns hook rather than per cell. */
  tooltipPadding: number;
}

/** Current funding rate as a signed, sign-colored percent (green positive / red negative), 4 dp. */
export function FundingRateCell({ value, data, periodLabel, tooltipPadding }: FundingRateCellProps) {
  if (value == null) {
    return (
      <Text as="span" color="colorTextSubtle">
        {NO_VALUE_PLACEHOLDER}
      </Text>
    );
  }
  const flat = isFlatRate(value);
  const content = (
    <InlineFormattedNumber
      number={value}
      increment={FUNDING_RATE_DISPLAY_INCREMENT}
      currency="%"
      variant={flat ? undefined : rateSignVariant(value)}
    />
  );
  if (data == null) {
    return content;
  }
  return (
    <FundingRateHoverTooltip
      symbol={data.symbol}
      exchangeDisplayName={data.exchangeDisplayName}
      fundingRatePct={value}
      openInterestUsd={data.openInterestUsd}
      periodLabel={periodLabel}
      tooltipPadding={tooltipPadding}
      targetStyle={LIST_TOOLTIP_TARGET_STYLE}
    >
      {content}
    </FundingRateHoverTooltip>
  );
}

/** Open interest column cell — blank when there's no value (distinct from the tooltip's "—" row). */
export function OpenInterestCell({ value }: ICellRendererParams<FundingRateRow, number | null>) {
  if (value == null) {
    return null;
  }
  return <InlineFormattedNumber number={value} {...OPEN_INTEREST_NUMBER_PROPS} />;
}

/** Rounded, centered chip filling a Matrix cell; background carries sign + magnitude, text is the rate. */
const HeatChip = styled.div<{ $background: string }>`
  background: ${({ $background }) => $background};
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  padding: ${({ theme }) => theme.spacing8}px ${({ theme }) => theme.spacing12}px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface MatrixRateCellProps {
  value: number | null;
  /** Largest |rate| in the snapshot — the heatmap normalization reference. */
  maxAbsRate: number;
  positiveColor: string;
  negativeColor: string;
  symbol: string;
  exchangeDisplayName: string;
  openInterestUsd: number | null;
  /** Current toolbar update-mode, resolved to its display label (e.g. "1H"/"1D") — shown in the hover card. */
  periodLabel: string;
  /** Card padding, resolved once from the theme by the columns hook rather than per cell. */
  tooltipPadding: number;
}

export function MatrixRateCell({
  value,
  maxAbsRate,
  positiveColor,
  negativeColor,
  symbol,
  exchangeDisplayName,
  openInterestUsd,
  periodLabel,
  tooltipPadding,
}: MatrixRateCellProps) {
  if (value == null) {
    return null;
  }
  const flat = isFlatRate(value);
  const background = flat ? 'transparent' : fundingRateHeatColor(value, maxAbsRate, positiveColor, negativeColor);
  return (
    <FundingRateHoverTooltip
      symbol={symbol}
      exchangeDisplayName={exchangeDisplayName}
      fundingRatePct={value}
      openInterestUsd={openInterestUsd}
      periodLabel={periodLabel}
      tooltipPadding={tooltipPadding}
      targetStyle={MATRIX_TOOLTIP_TARGET_STYLE}
    >
      <HeatChip $background={background} data-sign={flat ? 'neutral' : value >= 0 ? 'positive' : 'negative'}>
        <InlineFormattedNumber
          number={value}
          increment={FUNDING_RATE_DISPLAY_INCREMENT}
          currency="%"
          isSmall
          variant={flat ? undefined : rateSignVariant(value)}
        />
      </HeatChip>
    </FundingRateHoverTooltip>
  );
}
